import React, { useState } from "react";
import { Edit, Save, X } from "lucide-react";
import SectionCard from "../../Component/RetUserComponents/SectionCard";
import toast from "react-hot-toast";
import { BASE_URL } from "../../config/api";
import axios from "axios";

export default function Account() {
  const authUserString = localStorage.getItem("authUserData");
  const authUser = authUserString ? JSON.parse(authUserString) : {};

  const [formData, setFormData] = useState({
    contact_email: authUser?.contact_email || "",
    zoho_org_id: authUser?.zoho_org_id || "",
    zoho_client_id: authUser?.zoho_client_id || "",
    zoho_client_secret: authUser?.zoho_client_secret || "",
    zoho_redirect_uri: authUser?.zoho_redirect_uri || "",
    zoho_refresh_token: authUser?.zoho_refresh_token || "",
    zoho_access_token: authUser?.zoho_access_token || "",
    pincode: authUser?.pincode,
    role: authUser?.role,
  });

  const [editMode, setEditMode] = useState(false);
  const [loading, setLoading] = useState(false);

  const hasZohoDetails =
  !!authUser?.zoho_org_id &&
  !!authUser?.zoho_client_id &&
  !!authUser?.zoho_client_secret &&
  !!authUser?.zoho_refresh_token;

  const [hideEdit, setHideEdit] = useState(hasZohoDetails);


  const handleChange = (e) => {
    setFormData((p) => ({ ...p, [e.target.name]: e.target.value }));
  };

  /* ---------------------------------------------------
     🔐 VERIFY ZOHO TOKEN (QUERY PARAMS ONLY)
  --------------------------------------------------- */
  const verifyZohoToken = async () => {

    try {

      const response =
        await axios.get(
          `${BASE_URL}zoho/update-token`,
          {
            params: {
              company_id:
                authUser?.id,

              zoho_access_token:
                formData.zoho_access_token,

              zoho_client_id:
                formData.zoho_client_id,

              zoho_client_secret:
                formData.zoho_client_secret,

              zoho_redirect_uri:
                formData.zoho_redirect_uri,

              zoho_refresh_token:
                formData.zoho_refresh_token,
            },

            headers: {
              Accept:
                "application/json",

              "Content-Type":
                "application/json",
            },
          }
        );

      return response.data;

    } catch (err) {

      console.error(
        err
      );

      return (
        err?.response?.data || {
          status:
            false,

          message:
            "Token verification failed",
        }
      );
    }
  };

  /* ---------------- SAVE DETAILS ---------------- */
  const saveDetails = async () => {

    try {

      const response =
        await axios.post(
          `${BASE_URL}common/update`,
          formData,
          {
            headers: {
              Accept:
                "application/json",

              "Content-Type":
                "application/json",
            },
          }
        );

      return response.data;

    } catch (err) {

      console.error(
        err
      );

      return (
        err?.response?.data || {
          status:
            false,

          message:
            "Failed to save details",
        }
      );
    }
  };

  /* ---------------- SAVE HANDLER ---------------- */
  const handleSave = async () => {
    try {
      setLoading(true);

      // 1️⃣ Verify Zoho
      const verifyRes = await verifyZohoToken();

      if (!verifyRes?.status) {
        toast.error(
          verifyRes?.message ||
            "Zoho verification failed. Please check credentials."
        );
        return;
      }

      toast.success("Zoho verified successfully");

      // 2️⃣ Save data
      const saveRes = await saveDetails();

      if (saveRes?.status) {
        toast.success("Details updated successfully");

        localStorage.setItem(
          "authUserData",
          JSON.stringify({ ...authUser, ...formData })
        );

        setEditMode(false);
        setHideEdit(true); // ✅ HIDE EDIT BUTTON AFTER SUCCESS
      } else {
        toast.error(saveRes?.message || "Update failed");
      }
    } catch (err) {
      console.error(err);
      toast.error("Something went wrong");
    } finally {
      setLoading(false);
    }
  };

  /* ---------------- UI ---------------- */
  return (
    <SectionCard title="Update Zoho Details">
      <div className="flex justify-end mb-2">
        {/* ✅ UPDATED CONDITION */}
        {!editMode && !hideEdit ? (
          <button
            onClick={() => setEditMode(true)}
            className="text-xs flex items-center gap-1 bg-blue-600 text-white px-3 py-1.5 rounded-lg"
          >
            <Edit size={14} /> Edit
          </button>
        ) : editMode ? (
          <div className="flex gap-2">
            <button
              onClick={handleSave}
              disabled={loading}
              className="text-xs flex items-center gap-1 bg-green-600 text-white px-3 py-1.5 rounded-lg disabled:opacity-50"
            >
              <Save size={14} />
              {loading ? " Verifying & Saving..." : " Save"}
            </button>
            <button
              onClick={() => setEditMode(false)}
              className="text-xs flex items-center gap-1 bg-gray-500 text-white px-3 py-1.5 rounded-lg"
            >
              <X size={14} /> Cancel
            </button>
          </div>
        ) : null}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-sm">
        <ProfileField label="Contact Email">
          <Input
            name="contact_email"
            value={formData.contact_email}
            onChange={handleChange}
            disabled={!editMode}
          />
        </ProfileField>

        <ProfileField label="Zoho Org ID">
          <Input
            name="zoho_org_id"
            value={formData.zoho_org_id}
            onChange={handleChange}
            disabled={!editMode}
          />
        </ProfileField>

        <ProfileField label="Zoho Client ID">
          <Input
            name="zoho_client_id"
            value={formData.zoho_client_id}
            onChange={handleChange}
            disabled={!editMode}
          />
        </ProfileField>

        <ProfileField label="Zoho Client Secret">
          <Input
            name="zoho_client_secret"
            value={formData.zoho_client_secret}
            onChange={handleChange}
            disabled={!editMode}
          />
        </ProfileField>

        <ProfileField label="Zoho Redirect URI">
          <Input
            name="zoho_redirect_uri"
            value={formData.zoho_redirect_uri}
            onChange={handleChange}
            disabled={!editMode}
          />
        </ProfileField>
        <ProfileField label="Zoho access Token">
          <Input
            name="zoho_access_token"
            value={formData.zoho_access_token}
            onChange={handleChange}
            disabled={!editMode}
          />
        </ProfileField>

        <ProfileField label="Zoho Refresh Token">
          <Input
            name="zoho_refresh_token"
            value={formData.zoho_refresh_token}
            onChange={handleChange}
            disabled={!editMode}
          />
        </ProfileField>
      </div>
    </SectionCard>
  );
}

/* ---------------- reusable ---------------- */

function ProfileField({ label, children }) {
  return (
    <div className="space-y-1">
      <div className="text-sm text-slate-800">{label}</div>
      {children}
    </div>
  );
}

function Input({ name, value, onChange, disabled }) {
  return (
    <input
      name={name}
      value={value}
      onChange={onChange}
      disabled={disabled}
      className={`text-xs px-3 py-1.5 rounded-lg border w-full outline-none
        ${
          disabled
            ? "bg-slate-100 cursor-not-allowed"
            : "border-blue-400 focus:ring focus:ring-blue-200"
        }`}
    />
  );
}



// import React, { useState } from "react";
// import { Edit, Save, X } from "lucide-react";
// import SectionCard from "../../Component/RetUserComponents/SectionCard";
// import toast from "react-hot-toast";
// import { BASE_URL } from "../../config/api";

// export default function Account() {
//   const authUserString = localStorage.getItem("authUserData");
//   const authUser = authUserString ? JSON.parse(authUserString) : {};

//   const [formData, setFormData] = useState({
//     contact_email: authUser?.contact_email || "",
//     zoho_org_id: authUser?.zoho_org_id || "",
//     zoho_client_id: authUser?.zoho_client_id || "",
//     zoho_client_secret: authUser?.zoho_client_secret || "",
//     zoho_redirect_uri: authUser?.zoho_redirect_uri || "",
//     zoho_refresh_token: authUser?.zoho_refresh_token || "",
//     zoho_access_token: authUser?.zoho_access_token || "",
//     pincode: authUser?.pincode,
//     role: authUser?.role,
//   });

//   const [editMode, setEditMode] = useState(false);
//   const [loading, setLoading] = useState(false);

//   const handleChange = (e) => {
//     setFormData((p) => ({ ...p, [e.target.name]: e.target.value }));
//   };

//   /* ---------------------------------------------------
//      🔐 VERIFY ZOHO TOKEN (QUERY PARAMS ONLY)
//   --------------------------------------------------- */
//   const verifyZohoToken = async () => {
//     const params = new URLSearchParams({
//       company_id: authUser?.id,
//       zoho_client_id: formData.zoho_client_id,
//       zoho_client_secret: formData.zoho_client_secret,
//       zoho_redirect_uri: formData.zoho_redirect_uri,
//       zoho_refresh_token: formData.zoho_refresh_token,
//     });

//     const url = `${BASE_URL}zoho/update-token?${params.toString()}`;

//     const res = await fetch(url, {
//       method: "GET",
//       headers: { "Content-Type": "application/json" },
//     });

//     return res.json();
//   };

//   /* ---------------- SAVE DETAILS ---------------- */
//   const saveDetails = async () => {
//     const res = await fetch(`${BASE_URL}common/update`, {
//       method: "POST",
//       headers: { "Content-Type": "application/json" },
//       body: JSON.stringify(formData),
//     });

//     return res.json();
//   };

//   /* ---------------- SAVE HANDLER ---------------- */
//   const handleSave = async () => {
//     try {
//       setLoading(true);

//       // 1️⃣ Verify Zoho
//       const verifyRes = await verifyZohoToken();

//       if (!verifyRes?.status) {
//         toast.error(
//           verifyRes?.message ||
//             "Zoho verification failed. Please check credentials."
//         );
//         return;
//       }

//       toast.success("Zoho verified successfully");

//       // 2️⃣ Save data
//       const saveRes = await saveDetails();

//       if (saveRes?.status) {
//         toast.success("Details updated successfully");

//         localStorage.setItem(
//           "authUserData",
//           JSON.stringify({ ...authUser, ...formData })
//         );

//         setEditMode(false);
//       } else {
//         toast.error(saveRes?.message || "Update failed");
//       }
//     } catch (err) {
//       console.error(err);
//       toast.error("Something went wrong");
//     } finally {
//       setLoading(false);
//     }
//   };

//   /* ---------------- UI ---------------- */
//   return (
//     <SectionCard title="Update Zoho Details">
//       <div className="flex justify-end mb-2">
//         {!editMode ? (
//           <button
//             onClick={() => setEditMode(true)}
//             className="text-xs flex items-center gap-1 bg-blue-600 text-white px-3 py-1.5 rounded-lg"
//           >
//             <Edit size={14} /> Edit
//           </button>
//         ) : (
//           <div className="flex gap-2">
//             <button
//               onClick={handleSave}
//               disabled={loading}
//               className="text-xs flex items-center gap-1 bg-green-600 text-white px-3 py-1.5 rounded-lg disabled:opacity-50"
//             >
//               <Save size={14} />
//               {loading ? " Verifying & Saving..." : " Save"}
//             </button>
//             <button
//               onClick={() => setEditMode(false)}
//               className="text-xs flex items-center gap-1 bg-gray-500 text-white px-3 py-1.5 rounded-lg"
//             >
//               <X size={14} /> Cancel
//             </button>
//           </div>
//         )}
//       </div>

//       <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-sm">
//         <ProfileField label="Contact Email">
//           <Input name="contact_email" value={formData.contact_email} onChange={handleChange} disabled={!editMode} />
//         </ProfileField>

//         <ProfileField label="Zoho Org ID">
//           <Input name="zoho_org_id" value={formData.zoho_org_id} onChange={handleChange} disabled={!editMode} />
//         </ProfileField>

//         <ProfileField label="Zoho Client ID">
//           <Input name="zoho_client_id" value={formData.zoho_client_id} onChange={handleChange} disabled={!editMode} />
//         </ProfileField>

//         <ProfileField label="Zoho Client Secret">
//           <Input name="zoho_client_secret" value={formData.zoho_client_secret} onChange={handleChange} disabled={!editMode} />
//         </ProfileField>

//         <ProfileField label="Zoho Redirect URI">
//           <Input name="zoho_redirect_uri" value={formData.zoho_redirect_uri} onChange={handleChange} disabled={!editMode} />
//         </ProfileField>

//         <ProfileField label="Zoho Refresh Token">
//           <Input name="zoho_refresh_token" value={formData.zoho_refresh_token} onChange={handleChange} disabled={!editMode} />
//         </ProfileField>
//       </div>
//     </SectionCard>
//   );
// }

// /* ---------------- reusable ---------------- */

// function ProfileField({ label, children }) {
//   return (
//     <div className="space-y-1">
//       <div className="text-sm text-slate-800">{label}</div>
//       {children}
//     </div>
//   );
// }

// function Input({ name, value, onChange, disabled }) {
//   return (
//     <input
//       name={name}
//       value={value}
//       onChange={onChange}
//       disabled={disabled}
//       className={`text-xs px-3 py-1.5 rounded-lg border w-full outline-none
//         ${
//           disabled
//             ? "bg-slate-100 cursor-not-allowed"
//             : "border-blue-400 focus:ring focus:ring-blue-200"
//         }`}
//     />
//   );
// }






// import React, { useState } from "react";
// import { Edit, Save, X } from "lucide-react";
// import SectionCard from "../../Component/RetUserComponents/SectionCard";
// import toast from "react-hot-toast";
// import { BASE_URL } from "../../config/api"; // update path if needed

// export default function Account() {

//   const authUserString = localStorage.getItem("authUserData");
//   const authUser = authUserString ? JSON.parse(authUserString) : {};
//   console.log("")

//   const [formData, setFormData] = useState({
//     contact_email: authUser?.contact_email || "",
//     zoho_access_token: authUser?.zoho_access_token || "",
//     zoho_org_id: authUser?.zoho_org_id || "",
//     zoho_client_id: authUser?.zoho_client_id || "1000.PBQ91G33J26BXZE42RHY8HD391LPRI" || "",
//     zoho_client_secret: authUser?.zoho_client_secret || "dec05cc6a7e2c2d1ddedc375032e7821dcab73114d" || "",
//     zoho_redirect_uri: authUser?.zoho_redirect_uri || "http://www.zoho.com/books" || "",
//     zoho_refresh_token: authUser?.zoho_refresh_token || "1000.0996897ca4432bd1d148da533bfbd21d.b5b7dabc1ea76a2dda0d2ab1ee6df8cc"|| "",
//   });

//   const [editMode, setEditMode] = useState(false);
//   const [loading, setLoading] = useState(false);

//   const handleChange = (e) => {
//     setFormData({ ...formData, [e.target.name]: e.target.value });
//   };

//   const handleSave = async () => {
//     try {
//       setLoading(true);

//       const response = await fetch(`${BASE_URL}common/update`, {
//         method: "POST",
//         headers: { "Content-Type": "application/json" },
//         body: JSON.stringify(formData),
//       });

//       const result = await response.json();
//       console.log("API Response: ", result);

//       if (result?.status === true) {
//         toast.success("Details updated successfully");

//         // store updated data
//         localStorage.setItem("authUserData", JSON.stringify(formData));

//         setEditMode(false);
//       } else {
//         toast.error(result?.message || "Update failed");
//       }

//     } catch (err) {
//       toast.error("Something went wrong");
//       console.error(err);
//     } finally {
//       setLoading(false);
//     }
//   };

//   return (
//     <SectionCard title="Update Zoho Details">

//       {/* Header Actions */}
//       <div className="flex justify-end mb-2">
//         {!editMode ? (
//           <button
//             onClick={() => setEditMode(true)}
//             className="text-xs flex items-center gap-1 bg-blue-600 hover:bg-blue-700 text-white px-3 py-1.5 rounded-lg transition"
//           >
//             <Edit size={14} /> Edit
//           </button>
//         ) : (
//           <div className="flex gap-2">
//             <button
//               onClick={handleSave}
//               disabled={loading}
//               className="text-xs flex items-center gap-1 bg-green-600 hover:bg-green-700 text-white px-3 py-1.5 rounded-lg transition disabled:opacity-50"
//             >
//               <Save size={14} /> {loading ? "Saving..." : "Save"}
//             </button>
//             <button
//               onClick={() => setEditMode(false)}
//               className="text-xs flex items-center gap-1 bg-gray-500 hover:bg-gray-600 text-white px-3 py-1.5 rounded-lg transition"
//             >
//               <X size={14} /> Cancel
//             </button>
//           </div>
//         )}
//       </div>

//       {/* Form - Styled like Profile fields */}
//       <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-sm">

//         <ProfileField label="Contact Email">
//           <Input name="contact_email" value={formData.contact_email} onChange={handleChange} disabled={!editMode} />
//         </ProfileField>

//         <ProfileField label="Zoho Org ID">
//           <Input name="zoho_org_id" value={formData.zoho_org_id} onChange={handleChange} disabled={!editMode} />
//         </ProfileField>

//         <ProfileField label="Zoho Client ID">
//           <Input name="zoho_client_id" value={formData.zoho_client_id} onChange={handleChange} disabled={!editMode} />
//         </ProfileField>

//         <ProfileField label="Zoho Client Secret">
//           <Input name="zoho_client_secret" value={formData.zoho_client_secret} onChange={handleChange} disabled={!editMode} />
//         </ProfileField>

//         <ProfileField label="Zoho Redirect URI">
//           <Input name="zoho_redirect_uri" value={formData.zoho_redirect_uri} onChange={handleChange} disabled={!editMode} />
//         </ProfileField>

//         <ProfileField label="Zoho Access Token">
//           <Input name="zoho_access_token" value={formData.zoho_access_token} onChange={handleChange} disabled={!editMode} />
//         </ProfileField>
//       </div>
//     </SectionCard>
//   );
// }

// function ProfileField({ label, children }) {
//   return (
//     <div className="space-y-1">
//       <div className="text-sm text-slate-800">{label}</div>
//       {children}
//     </div>
//   );
// }

// function Input({ name, value, onChange, disabled }) {
//   return (
//     <input
//       name={name}
//       value={value}
//       onChange={onChange}
//       disabled={disabled}
//       className={`text-xs px-3 py-1.5 rounded-lg border w-full outline-none transition
//         ${disabled ? "text-gray-700 cursor-not-allowed" : "border-blue-400 focus:ring focus:ring-blue-200"}
//       `}
//     />
//   );
// }

